"use client";

import type {
  AccountLookupOutput,
  AddressSearchOutput,
} from "@tillix/api-contract/client-schemas";
import { Button } from "@tillix/ui-atoms/button";
import { formatCurrency, formatDate } from "@tillix/utils/format";
import { observer } from "mobx-react-lite";
import { useT } from "@/features/i18n/hooks/use-t";

type SearchResult = AddressSearchOutput["results"][number];

type AccountLookupResultsProps =
  | { result: AccountLookupOutput; onPayNow?: () => void }
  | {
      results: SearchResult[];
      onSelect: (account: SearchResult) => void;
      maskAccount?: boolean;
    };

function AccountLookupResults(props: AccountLookupResultsProps) {
  if ("results" in props) {
    return (
      <SearchResultsList
        maskAccount={props.maskAccount}
        onSelect={props.onSelect}
        results={props.results}
      />
    );
  }

  return <AccountDetail onPayNow={props.onPayNow} result={props.result} />;
}

function SearchResultsList({
  results,
  onSelect,
  maskAccount = false,
}: {
  results: SearchResult[];
  onSelect: (account: SearchResult) => void;
  maskAccount?: boolean;
}) {
  const { t } = useT();
  return (
    <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
      <div className="border-b bg-muted/30 px-5 py-3">
        <h2 className="font-semibold text-sm">
          {results.length}{" "}
          {results.length !== 1
            ? t("quickPay.accountsFound")
            : t("quickPay.accountFound")}{" "}
          {t("quickPay.found")}
        </h2>
      </div>

      <ul className="divide-y">
        {results.map((account) => {
          const streetLine = [
            account.address.houseNumber,
            account.address.street,
          ]
            .filter(Boolean)
            .join(" ");
          const addressParts = [
            account.address.aptNumber
              ? `${streetLine}, ${account.address.aptNumber}`
              : streetLine,
            [account.address.city, account.address.state, account.address.zip]
              .filter(Boolean)
              .join(", "),
          ].filter(Boolean);

          return (
            <li
              className="flex items-center justify-between gap-4 px-5 py-3"
              key={`${account.contractAccount}-${account.address.aptNumber ?? ""}`}
            >
              <div className="min-w-0 flex-1">
                {addressParts.length > 0 && (
                  <p className="font-medium text-sm">
                    {addressParts.join(", ")}
                  </p>
                )}
                <p className="text-muted-foreground text-xs">
                  {t("quickPay.accountNumber")}:{" "}
                  {maskAccount
                    ? `***${account.contractAccount.slice(-4)}`
                    : account.contractAccount}
                </p>
              </div>
              <Button
                className="shrink-0"
                onClick={() => onSelect(account)}
                size="sm"
                variant="outline"
              >
                {t("application.select")}
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AccountDetail({
  result,
  onPayNow,
}: {
  result: AccountLookupOutput;
  onPayNow?: () => void;
}) {
  const { t } = useT();
  const formattedBalance = formatCurrency(result.balance);

  const formattedDueDate = result.dueDate
    ? formatDate(new Date(result.dueDate))
    : null;

  const addressParts = [
    result.address.street1,
    result.address.street2,
    [result.address.city, result.address.state, result.address.zip]
      .filter(Boolean)
      .join(", "),
  ].filter(Boolean);

  return (
    <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
      <div className="border-b bg-muted/30 px-5 py-3">
        <h2 className="font-semibold text-lg">
          {t("quickPay.accountDetails")}
        </h2>
      </div>

      <div className="divide-y">
        {result.maskedContractAccount && (
          <div className="px-5 py-3">
            <span className="text-muted-foreground text-xs uppercase tracking-wide">
              {t("quickPay.contractAccount")}
            </span>
            <p className="mt-1 font-medium">{result.maskedContractAccount}</p>
          </div>
        )}

        {!result.maskedContractAccount && result.accountHolder && (
          <div className="px-5 py-3">
            <span className="text-muted-foreground text-xs uppercase tracking-wide">
              {t("quickPay.accountHolder")}
            </span>
            <p className="mt-1 font-medium">{result.accountHolder}</p>
          </div>
        )}

        <div className="px-5 py-3">
          <span className="text-muted-foreground text-xs uppercase tracking-wide">
            {t("quickPay.currentBalance")}
          </span>
          <p className="mt-1 font-bold text-2xl tracking-tight">
            {formattedBalance}
          </p>
        </div>

        {formattedDueDate && (
          <div className="px-5 py-3">
            <span className="text-muted-foreground text-xs uppercase tracking-wide">
              {t("quickPay.dueDate")}
            </span>
            <p className="mt-1 font-medium">{formattedDueDate}</p>
          </div>
        )}

        {addressParts.length > 0 && (
          <div className="px-5 py-3">
            <span className="text-muted-foreground text-xs uppercase tracking-wide">
              {t("quickPay.serviceAddress")}
            </span>
            <div className="mt-1">
              {addressParts.map((line) => (
                <p className="font-medium leading-relaxed" key={line}>
                  {line}
                </p>
              ))}
            </div>
          </div>
        )}

        {result.meterNo && (
          <div className="px-5 py-3">
            <span className="text-muted-foreground text-xs uppercase tracking-wide">
              {t("quickPay.meterNumber")}
            </span>
            <p className="mt-1 font-medium">{result.meterNo}</p>
          </div>
        )}

        {onPayNow && (
          <div className="flex flex-row-reverse px-5 py-3">
            <Button disabled={result.balance <= 0} onClick={onPayNow}>
              {t("quickPay.payNow")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default observer(AccountLookupResults);
